// owner/screens/Overview.js — «نظرة عامة» (/dashboard), as signed off in the mock-ups
// (docs/02_Product/design_system/owner-app-mockups/overview-phone.html, overview-desktop.html).
// Each part loads on its own: a slow or failed part shows a skeleton or an error with a retry in its place,
// never a zero (OWNER_EXPERIENCE_MAP). Every period and day comes from the server (`thisMonth`, `last6Months`,
// owner-week); the page does no date arithmetic. Not shown, by decision: "vs last month", a bell, departure times.

import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import useLanguage from '../../hooks/useLanguage';
import { fill, joinList } from '../../utils/localeText';
import PlatformMark, { usePlatformName } from '../ui/PlatformMark';
import BookingRow from '../ui/BookingRow';
import UnitPicker from '../ui/UnitPicker';
import { SkeletonBlocks, ErrorState, InlineError, EmptyState } from '../ui/States';
import useOwnerFormat from '../ui/useOwnerFormat';
import { useOwnerProfile, useOwnerUnits, usePortfolio, useOwnerWeek } from '../api/ownerReads';
import AlertsInvite from '../alerts/AlertsInvite';

// Platform shares in the brand purple, strongest first (mock-up legend).
const SHARE_COLOURS = ['var(--purple)', 'var(--purple-70)', 'var(--purple-45)', 'var(--purple-22)', 'var(--g300)'];

const SectionError = ({ text, onRetry }) => <div className="o-sheet"><InlineError text={text} onRetry={onRetry} /></div>;

const MonthCard = ({ query }) => {
  const { t } = useLanguage();
  const f = useOwnerFormat();
  if (query.isLoading) return <div className="o-sk o-sk-hero" aria-busy="true" />;
  if (query.isError || !query.data?.metrics) {
    return <section className="o-section"><SectionError text={t('ownerApp.overview.monthError')} onRetry={query.refetch} /></section>;
  }
  const { metrics } = query.data;
  const monthKey = metrics.byMonth?.[metrics.byMonth.length - 1]?.month;
  const monthName = monthKey ? f.monthName(monthKey) : '';
  const [before, after] = t('ownerApp.overview.monthRevenue').split('{month}');
  return (
    <section className="o-hero" aria-label={fill(t('ownerApp.overview.monthRevenue'), { month: monthName })}>
      <svg className="o-house-bg" viewBox="0 0 24 24" aria-hidden="true">
        <path fill="currentColor" d="M3 10.6 10.9 4.5a1.8 1.8 0 0 1 2.2 0L21 10.6V18.5a2.5 2.5 0 0 1-2.5 2.5h-13A2.5 2.5 0 0 1 3 18.5Z" />
      </svg>
      <p className="o-label">{before}<span className="o-month">{monthName}</span>{after}</p>
      <p className="o-value"><span className="o-money">{f.amount(metrics.revenue?.gross ?? 0)}</span><span className="o-cur">{f.currency}</span></p>
      <div className="o-stats">
        <div className="o-stat">
          <p className="o-n"><span className="o-money">{f.percent(metrics.occupancy?.rate ?? 0)}</span></p>
          <p className="o-l">{t('ownerApp.overview.occupancy')}</p>
        </div>
        <div className="o-stat">
          <p className="o-n"><span className="o-money">{f.count(metrics.bookings?.total ?? 0)}</span></p>
          <p className="o-l">{fill(t('ownerApp.overview.monthBookings'), { month: monthName })}</p>
        </div>
      </div>
    </section>
  );
};

const Trend = ({ query }) => {
  const { t, language } = useLanguage();
  const f = useOwnerFormat();
  const months = query.data?.metrics?.byMonth || [];
  const max = Math.max(...months.map((m) => m.grossRevenue || 0), 0);
  // Thousands: whole numbers from 10k, one decimal below, so a small month is not shown as 0.
  const inThousands = (v) => (v >= 10000 ? Math.round(v / 1000) : Math.round(v / 100) / 10);
  return (
    <section className="o-section">
      <div className="o-section-head">
        <h2>{t('ownerApp.overview.trendTitle')}</h2>
        <span className="o-aside">{fill(t('ownerApp.overview.trendUnit'), { currency: f.currency })}</span>
      </div>
      {query.isLoading && <div className="o-sk" style={{ height: 172, borderRadius: 20 }} aria-busy="true" />}
      {!query.isLoading && (query.isError || !query.data?.metrics) && (
        <SectionError text={t('ownerApp.overview.trendError')} onRetry={query.refetch} />
      )}
      {!query.isLoading && query.data?.metrics && (
        <div className="o-sheet">
          {/* Oldest to newest in the reading direction: June on the right in Arabic, on the left in English (Adil). */}
          <div className="o-bars" role="img"
            aria-label={joinList(months.map((m) => `${f.monthName(m.month)} ${f.money(m.grossRevenue)}`), language)}>
            {months.map((m, i) => (
              <div key={m.month} className={`o-bar${i === months.length - 1 ? ' cur' : ''}`}>
                <span className="o-v">{f.count(inThousands(m.grossRevenue || 0))}</span>
                <i style={{ height: `${max > 0 ? Math.max(2, ((m.grossRevenue || 0) / max) * 100) : 2}%` }} />
              </div>
            ))}
          </div>
          <div className="o-bars-x">
            {months.map((m, i) => (
              <span key={m.month} className={i === months.length - 1 ? 'cur' : undefined}>{f.monthName(m.month)}</span>
            ))}
          </div>
        </div>
      )}
    </section>
  );
};

const Platforms = ({ query }) => {
  const { t, language } = useLanguage();
  const f = useOwnerFormat();
  const nameOf = usePlatformName();
  const metrics = query.data?.metrics;
  const monthKey = metrics?.byMonth?.[metrics.byMonth.length - 1]?.month;
  const rows = (metrics?.byPlatform || []).filter((p) => p.grossRevenue > 0).sort((a, b) => b.grossRevenue - a.grossRevenue);
  const total = rows.reduce((sum, p) => sum + p.grossRevenue, 0);
  return (
    <section className="o-section">
      <div className="o-section-head">
        <h2>{t('ownerApp.overview.byPlatform')}</h2>
        {monthKey && <span className="o-aside">{f.monthName(monthKey)}</span>}
      </div>
      {query.isLoading && <div className="o-sk" style={{ height: 150, borderRadius: 20 }} aria-busy="true" />}
      {!query.isLoading && (query.isError || !metrics) && <SectionError text={t('ownerApp.overview.monthError')} onRetry={query.refetch} />}
      {!query.isLoading && metrics && total === 0 && (
        <div className="o-sheet"><p className="o-empty-row">{t('ownerApp.overview.noRevenueYet')}</p></div>
      )}
      {!query.isLoading && metrics && total > 0 && (
        <div className="o-sheet o-split">
          <div className="o-split-bar" role="img"
            aria-label={joinList(rows.map((p) => `${nameOf(p.platform)} ${f.percent((p.grossRevenue / total) * 100)}`), language)}>
            {rows.map((p, i) => (
              <i key={p.platform} style={{ width: `${(p.grossRevenue / total) * 100}%`, background: SHARE_COLOURS[Math.min(i, SHARE_COLOURS.length - 1)] }} />
            ))}
          </div>
          <ul className="o-legend">
            {rows.map((p, i) => (
              <li key={p.platform}>
                <span className="o-sw" style={{ background: SHARE_COLOURS[Math.min(i, SHARE_COLOURS.length - 1)] }} />
                <span className="o-name"><PlatformMark platform={p.platform} withName /></span>
                <span className="o-amt o-money">{f.money(p.grossRevenue)}</span>
                <span className="o-pct o-money">{f.percent((p.grossRevenue / total) * 100)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
};

const WeekList = ({ query, kind, photoById }) => {
  const { t } = useLanguage();
  const f = useOwnerFormat();
  const rows = query.data?.[kind] || [];
  const total = query.data?.[`${kind}Total`] ?? rows.length;
  const isArrivals = kind === 'arrivals';
  return (
    <section className="o-section">
      <div className="o-section-head">
        <h2>{t(`ownerApp.overview.${kind}`)}</h2>
        {isArrivals
          ? <Link className="o-link" to="/bookings">{t('ownerApp.overview.allBookings')}</Link>
          : <span className="o-aside">{t('ownerApp.overview.nextSevenDays')}</span>}
      </div>
      {query.isLoading && <div className="o-sk" style={{ height: 150, borderRadius: 20 }} aria-busy="true" />}
      {!query.isLoading && query.isError && <SectionError text={t('ownerApp.overview.weekError')} onRetry={query.refetch} />}
      {!query.isLoading && !query.isError && (
        <div className="o-sheet">
          {rows.length === 0
            ? <p className="o-empty-row">{t(isArrivals ? 'ownerApp.overview.noArrivals' : 'ownerApp.overview.noDepartures')}</p>
            : (
              <ul className="o-rows">
                {rows.map((b, i) => (
                  <BookingRow key={b._id} booking={b} variant={isArrivals ? 'arrival' : 'departure'}
                    photo={photoById[String(b.property?._id)]} index={i} />
                ))}
              </ul>
            )}
          {total > rows.length && (
            <p className="o-list-foot">{fill(t('ownerApp.overview.shownOf'), { shown: f.count(rows.length), total: f.count(total) })}</p>
          )}
        </div>
      )}
    </section>
  );
};

const Overview = () => {
  const { t } = useLanguage();
  const f = useOwnerFormat();
  const [unitId, setUnitId] = useState('');  // '' = all units (plan §10: all units by default)
  const me = useOwnerProfile();
  const units = useOwnerUnits();
  const month = usePortfolio('thisMonth', unitId);
  const trend = usePortfolio('last6Months', unitId);
  const week = useOwnerWeek(unitId);

  const unitList = useMemo(() => (Array.isArray(units.data) ? units.data : []), [units.data]);
  const photoById = useMemo(
    () => Object.fromEntries(unitList.map((u) => [String(u._id), u.bookingPageSettings?.primaryPhoto])),
    [unitList]
  );

  if (units.isLoading) return <SkeletonBlocks hero blocks={3} />;
  if (units.isError) return <ErrorState title={t('ownerApp.overview.loadError')} onRetry={units.refetch} />;
  if (unitList.length === 0) {
    return <EmptyState icon="units" title={t('ownerApp.overview.noUnitsTitle')} hint={t('ownerApp.overview.noUnitsHint')} />;
  }

  return (
    <>
      <div className="o-page-title">
        <h1>{me.data?.name ? fill(t('ownerApp.overview.greeting'), { name: me.data.name.split(' ')[0] }) : t('ownerApp.nav.overview')}</h1>
        <p className="o-sub">{fill(t('ownerApp.overview.unitsManaged'), { units: f.units(unitList.length) })}</p>
        <span className="o-underline" />
      </div>

      <AlertsInvite />

      {unitList.length > 1 && <UnitPicker units={unitList} value={unitId} onChange={setUnitId} />}

      <div className="o-overview-grid">
        <div className="o-col">
          <MonthCard query={month} />
          <Trend query={trend} />
        </div>
        <div className="o-col">
          <Platforms query={month} />
        </div>
      </div>
      <div className="o-two">
        <WeekList query={week} kind="arrivals" photoById={photoById} />
        <WeekList query={week} kind="departures" photoById={photoById} />
      </div>
    </>
  );
};

export default Overview;
